<script lang="ts" setup>
import { onMounted, ref } from 'vue';

import {
  downloadCallLogHistory,
  fetchCallLogHistoryFiles,
} from '@/api/apiService';
import InfoIcon from '@/components/icons/IconInfo.vue';
import LoadingIcon from '@/components/icons/IconLoading.vue';
import TableDynamic from '@/components/shared/base/TableDynamic.vue';
import ModalSmall from '@/components/shared/composite/ModalSmall.vue';
import { useAllTranslations } from '@/static/messageBylanguages.js';
import { useToastStore } from '@/stores/toast.js';
import { TypeCall } from '@/types/common';

const props = withDefaults(
  defineProps<{
    code: string;
    callType?: TypeCall;
  }>(),
  {
    code: '',
    callType: '',
  }
);

const emit = defineEmits(['close']);
const toastStore = useToastStore();
const t = useAllTranslations();

const close = () => {
  emit('close');
};

const columns = [
  {
    label: t.call_date_time.value,
    key: 'datetime',
    type: 'text',
    class: 'width100 paddingleft',
  },
  {
    label: ' ',
    key: 'download',
    type: 'action',
    action: 'download',
    actionText: t.download.value,
    class: 'width5rem',
  },
];

const rows = ref([]);
const loading = ref(false);
const loadingIndex = ref(-1);
const codeCallHistory = ref(props.callType === 'AUDIENCE' ? null : props.code);

const download = async (data) => {
  if (loadingIndex.value !== -1) {
    toastStore.warn(t.download_in_progress.value);
    return;
  }
  loadingIndex.value = data.index;
  await downloadCallLogHistory(data.row.id, data.row.filename);
  loadingIndex.value = -1;
};

const requestCallHistory = async () => {
  loading.value = true;
  if (props.code && props.callType) {
    const { data = [], success } = await fetchCallLogHistoryFiles(
      props.callType,
      {
        code: codeCallHistory.value,
      }
    );
    if (success) {
      rows.value = data;
    }
  }
  loading.value = false;
};

onMounted(async () => {
  await requestCallHistory();
});
</script>

<template>
  <ModalSmall :title="$t('download_call_history')" @close="close">
    <div class="info-wrap">
      <InfoIcon class="icon-dim icon-info" />
      <p class="small dim">{{ $t('call_history_retention_notice') }}</p>
    </div>
    <div v-if="loading" class="loading-wrap">
      <LoadingIcon />
    </div>
    <TableDynamic
      v-else
      :emptyMessage="$t('no_call_history')"
      showTagfilter="false"
      :columns="columns"
      :rows="rows"
      :showMoreButton="false"
      :loading-index="loadingIndex"
      @actionClick="download"
    />
  </ModalSmall>
</template>

<style scoped>
.info-wrap {
  display: flex;
  gap: 0.25rem;
  color: var(--color-gray-100);
  align-items: flex-start;
  margin-bottom: 1rem;
}

.loading-wrap {
  display: flex;
  gap: 0.5rem;
  color: var(--color-gray-100);
  margin-bottom: 1rem;
  justify-content: center;
}

.icon-info {
  position: relative;
  top: 0.125rem;
}

::v-deep(.dynamic-table) {
  min-width: unset;
}
</style>
